import { DefaultQueryParams } from '@frontend/api-utils';
import { ChartClient } from '@frontend/chart/api';
import { hasChartData } from '@frontend/chart/components';
import { Chart, ChartDefinition } from '@frontend/chart/types';
import { useEffect, useMemo, useState } from 'react';

import { getLast6MonthsChartRange } from './last-30-days-chart-range';

export const usePudoTransactionTimerWaitingDaysDonut = (accountId: string, definition: ChartDefinition) => {
    const [data, setData] = useState<Chart | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!accountId) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        // Backend defaults to ~30 days when after/before are omitted; awaiting cards need a wider window.
        const { after, before } = getLast6MonthsChartRange();

        ChartClient.fetchChartData(accountId, definition, {
            [DefaultQueryParams.AFTER]: after,
            [DefaultQueryParams.BEFORE]: before
        })
            .then((chart) => {
                if (!cancelled) {
                    setData(chart);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setData(undefined);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [accountId, definition]);

    const total = useMemo(() => {
        if (!data) {
            return 0;
        }

        return data.series.flatMap((series) => series.data).reduce((sum, point) => sum + point.value, 0);
    }, [data]);

    return {
        data,
        isLoading,
        total,
        hasData: hasChartData(data)
    };
};
